import { Icon } from '@iconify/react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { RadioButton, TextBox } from '@progress/kendo-react-inputs';
import { Error, Label } from '@progress/kendo-react-labels';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AcceptIcon from 'src/assets/icons/AcceptIcon';
import ClearIcon from 'src/assets/icons/ClearIcon';
import TrashIcon from 'src/assets/icons/TrashIcon';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import LoadingPanel from 'src/components/LoadingPanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import { isValidEmail } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import useUserInfoSsoForm from '../../schemas/useUserInfoSsoForm';
import { ManageEmailsContext } from './ManageEmailsContext';

const UserInfo = () => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const userInfo = useContextSelector(ManageEmailsContext, (c) => c.userInfo);
  const displayEmail = useContextSelector(ManageEmailsContext, (c) => c.displayEmail);
  const setDisplayEmail = useContextSelector(ManageEmailsContext, (c) => c.setDisplayEmail);
  const manageAddresses = useContextSelector(ManageEmailsContext, (c) => c.manageAddresses);
  const selectedNotificationAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.selectedNotificationAddress
  );
  const setSelectedNotificationAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.setSelectedNotificationAddress
  );
  const editingId = useContextSelector(ManageEmailsContext, (c) => c.editingId);
  const editingDraft = useContextSelector(ManageEmailsContext, (c) => c.editingDraft);
  const setEditingDraft = useContextSelector(ManageEmailsContext, (c) => c.setEditingDraft);
  const onStartEditManageAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.onStartEditManageAddress
  );
  const onSaveManageAddress = useContextSelector(ManageEmailsContext, (c) => c.onSaveManageAddress);
  const onCancelManageAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.onCancelManageAddress
  );
  const onDeleteManageAddress = useContextSelector(
    ManageEmailsContext,
    (c) => c.onDeleteManageAddress
  );
  const onConfirmAddNotificationEmail = useContextSelector(
    ManageEmailsContext,
    (c) => c.onConfirmAddNotificationEmail
  );
  const isNotificationUserInfoLoading = useContextSelector(
    ManageEmailsContext,
    (c) => c.isNotificationUserInfoLoading
  );
  const notificationUserInfoError = useContextSelector(
    ManageEmailsContext,
    (c) => c.notificationUserInfoError
  );
  const isNotificationEmailsLoading = useContextSelector(
    ManageEmailsContext,
    (c) => c.isNotificationEmailsLoading
  );
  const notificationEmailsError = useContextSelector(
    ManageEmailsContext,
    (c) => c.notificationEmailsError
  );
  const updateClientEmail = useContextSelector(ManageEmailsContext, (c) => c.updateClientEmail);
  const isUpdatingClientEmail = useContextSelector(
    ManageEmailsContext,
    (c) => c.isUpdatingClientEmail
  );
  const isAddingNotificationEmail = useContextSelector(
    ManageEmailsContext,
    (c) => c.isAddingNotificationEmail
  );
  const isUpdatingNotificationEmail = useContextSelector(
    ManageEmailsContext,
    (c) => c.isUpdatingNotificationEmail
  );
  const isDeletingNotificationEmail = useContextSelector(
    ManageEmailsContext,
    (c) => c.isDeletingNotificationEmail
  );
  const isAddingNewEmail = useContextSelector(ManageEmailsContext, (c) => c.isAddingNewEmail);
  const setIsAddingNewEmail = useContextSelector(ManageEmailsContext, (c) => c.setIsAddingNewEmail);

  const {
    formik,
    isSsoEditing,
    handleSsoEditClick,
    handleSsoSaveClick,
    handleSsoCancelClick,
    createFormikChangeHandler,
    validationMessages,
    isSsoSaveEnabled,
  } = useUserInfoSsoForm({
    displayEmail,
    setDisplayEmail,
    onSaveEmail: updateClientEmail,
    t,
  });

  const [newEmailDraft, setNewEmailDraft] = useState('');
  const isAddressActionsLocked = editingId !== null || isAddingNewEmail;
  const hasInvalidDoubleQuote = useCallback((email: string) => email.includes('"'), []);
  const isValidNotificationEmail = useCallback(
    (email: string) => isValidEmail(email) && !hasInvalidDoubleQuote(email),
    [hasInvalidDoubleQuote]
  );

  /** Compares addresses case-insensitively (e.g. n@gmail.com === N@gmail.com). */
  const isNotificationEmailDuplicate = useCallback(
    (email: string, excludeId?: string) => {
      const normalized = email.trim().toLowerCase();
      if (!normalized) return false;
      return manageAddresses.some(
        (a) => a.id !== excludeId && a.notificationAddress.trim().toLowerCase() === normalized
      );
    },
    [manageAddresses]
  );

  const isNewEmailDuplicate = useMemo(
    () => isValidNotificationEmail(newEmailDraft) && isNotificationEmailDuplicate(newEmailDraft),
    [isNotificationEmailDuplicate, isValidNotificationEmail, newEmailDraft]
  );

  const handleAddNewClick = useCallback(() => {
    if (editingId !== null) return;
    setIsAddingNewEmail(true);
    setNewEmailDraft('');
  }, [editingId, setIsAddingNewEmail]);

  const handleConfirmAddNew = useCallback(async () => {
    const trimmed = newEmailDraft.trim();
    if (
      !trimmed ||
      !isValidNotificationEmail(newEmailDraft) ||
      isNotificationEmailDuplicate(trimmed)
    )
      return;
    try {
      await onConfirmAddNotificationEmail(trimmed);
      setNewEmailDraft('');
      setIsAddingNewEmail(false);
    } catch {
      // Error shown by axios interceptor; keep input open
    }
  }, [
    isNotificationEmailDuplicate,
    isValidNotificationEmail,
    newEmailDraft,
    onConfirmAddNotificationEmail,
    setIsAddingNewEmail,
  ]);

  const handleCancelAddNew = useCallback(() => {
    setNewEmailDraft('');
    setIsAddingNewEmail(false);
  }, [setIsAddingNewEmail]);

  const handleDelete = useCallback(
    async (item: { id: string; notificationAddress: string }) => {
      const isDeleteAccepted = await confirm({
        title: t('deleteConfirmationTitle'),
        content: (
          <div className="text-sm break-all [overflow-wrap:anywhere]">
            {t('deleteConfirmationMessage', { email: item.notificationAddress })}
          </div>
        ),
        width: '420px',
        confirmButtonText: t('yesDelete'),
        cancelButtonText: t('noCancel'),
      });
      if (isDeleteAccepted) {
        try {
          await onDeleteManageAddress(item.id);
        } catch {
          // Error shown by axios interceptor
        }
      }
    },
    [confirm, onDeleteManageAddress, t]
  );

  return (
    <div
      className="flex min-w-0 flex-col w-full border border-gray-300"
      style={{ minHeight: '400px', boxSizing: 'border-box' }}
    >
      <div className="flex items-center justify-between gap-3 px-4 h-10 bg-[#3F6578] w-full flex-shrink-0">
        <h2 className="text-white text-sm font-medium">{t('userInfo')}</h2>
      </div>
      <div className="flex-1 min-h-0 bg-white overflow-auto p-4 flex flex-col gap-4">
        <div>
          <div className="text-sm text-gray-900">{t('Company Name')}</div>
          <div className="text-sm font-semibold text-gray-900 mt-0.5">{userInfo.companyName}</div>
        </div>

        <div>
          <div className="text-sm text-gray-900">{t('contactName')}</div>
          <div className="text-sm font-semibold text-gray-900 mt-0.5">{userInfo.contactName}</div>
        </div>

        <div>
          {isNotificationUserInfoLoading ? (
            <div>
              <div className="text-sm text-gray-900">{t('ssoOrPasswordResetEmailAddress')}</div>
              <div className="text-sm text-gray-500 mt-0.5">{t('loadingEmail')}</div>
            </div>
          ) : notificationUserInfoError ? (
            <div>
              <div className="text-sm text-gray-900">{t('ssoOrPasswordResetEmailAddress')}</div>
              <div className="text-sm text-red-600 mt-0.5">
                {(notificationUserInfoError as Error)?.message || t('errorLoadingUserInfo')}
              </div>
            </div>
          ) : !isSsoEditing ? (
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="text-sm text-gray-900">{t('ssoOrPasswordResetEmailAddress')}</div>
                <div className="text-sm font-semibold text-gray-900 mt-0.5">{displayEmail}</div>
              </div>
              <button
                type="button"
                onClick={handleSsoEditClick}
                className="flex flex-shrink-0 items-center gap-1.5 p-0.5 text-sm text-info hover:underline"
                aria-label={t('Edit')}
              >
                <Icon
                  icon="mdi:pencil"
                  width={16}
                  height={16}
                  className="flex-shrink-0 text-info"
                />
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <FieldWrapper>
                <Label editorId="user-info-sso-email" className="text-sm text-gray-900">
                  {t('ssoOrPasswordResetEmailAddress')} <span className="text-red-500">*</span>
                </Label>
                <TextBox
                  id="user-info-sso-email"
                  name="ssoEmail"
                  value={formik.values.ssoEmail}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  size="medium"
                  className={clsx(
                    'w-full',
                    formik.errors.ssoEmail &&
                      formik.touched.ssoEmail &&
                      '[&_.k-input-inner]:!border-error'
                  )}
                />
                {formik.errors.ssoEmail && formik.touched.ssoEmail && (
                  <Error id="user-info-sso-email">{formik.errors.ssoEmail}</Error>
                )}
              </FieldWrapper>
              <FieldWrapper>
                <Label editorId="user-info-confirm-sso-email" className="text-sm text-gray-900">
                  {t('confirmSsoEmailAddress')} <span className="text-red-500">*</span>
                </Label>
                <TextBox
                  id="user-info-confirm-sso-email"
                  name="confirmSsoEmail"
                  value={formik.values.confirmSsoEmail}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  size="medium"
                  className={clsx(
                    'w-full',
                    formik.errors.confirmSsoEmail &&
                      formik.touched.confirmSsoEmail &&
                      '[&_.k-input-inner]:!border-error'
                  )}
                />
                {formik.errors.confirmSsoEmail && formik.touched.confirmSsoEmail && (
                  <Error id="user-info-confirm-sso-email">{formik.errors.confirmSsoEmail}</Error>
                )}
              </FieldWrapper>
              <div className="flex justify-start gap-2">
                <PopUp
                  content={
                    validationMessages.length > 0 ? (
                      <div className="m-1 max-w-[300px]">
                        <PopUpContentWrapper type="Error" contents={validationMessages} />
                      </div>
                    ) : null
                  }
                  show={validationMessages.length > 0}
                  anchorAlign={{ horizontal: 'center', vertical: 'top' }}
                  popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
                >
                  <Button
                    type="button"
                    color="info"
                    size="md"
                    disabled={!isSsoSaveEnabled || isUpdatingClientEmail}
                    onClick={handleSsoSaveClick}
                    className="w-auto"
                  >
                    {t('Save')}
                  </Button>
                </PopUp>
                <Button
                  type="button"
                  color="secondary"
                  size="md"
                  onClick={handleSsoCancelClick}
                  className="w-auto"
                >
                  {t('Cancel')}
                </Button>
              </div>
            </div>
          )}
          <div className="flex gap-2 p-3 bg-warning-light border border-warning rounded text-sm mt-3">
            <Icon
              icon="lucide:triangle-alert"
              className="flex-shrink-0 text-amber-600"
              width={20}
              height={20}
            />
            <span className="text-[#733E0A]">{t('userInfoSsoWarning')}</span>
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-900">{t('notificationMethod')}</div>
          <div className="text-sm font-semibold text-gray-900 mt-0.5">{t('eMail')}</div>
        </div>

        <div>
          <div className="text-sm text-gray-900 mb-2">{t('notificationEmailAddress')}</div>
          {isNotificationEmailsLoading ? (
            <div className="relative min-h-[80px]">
              <LoadingPanel />
            </div>
          ) : notificationEmailsError ? (
            <div className="text-sm text-red-600 mt-0.5">
              {(notificationEmailsError as Error)?.message || t('errorLoadingNotificationEmails')}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {manageAddresses.map((item) => {
                const isEditing = editingId === item.id;
                const hasManageAddressChanged =
                  editingDraft.trim().toLowerCase() !==
                  item.notificationAddress.trim().toLowerCase();
                const isEditEmailDuplicate =
                  isValidNotificationEmail(editingDraft) &&
                  isNotificationEmailDuplicate(editingDraft, item.id);
                const canSaveManageAddress =
                  isValidNotificationEmail(editingDraft) &&
                  hasManageAddressChanged &&
                  !isEditEmailDuplicate &&
                  !isUpdatingNotificationEmail;
                return (
                  <div key={item.id} className="flex items-center gap-2 w-full min-h-[32px]">
                    <RadioButton
                      value={item.notificationAddress}
                      checked={selectedNotificationAddress === item.notificationAddress}
                      onChange={() => setSelectedNotificationAddress(item.notificationAddress)}
                      disabled={editingId !== null}
                    />
                    {isEditing ? (
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <input
                          type="text"
                          value={editingDraft}
                          onChange={(e) => setEditingDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && canSaveManageAddress)
                              onSaveManageAddress(item.id);
                            if (e.key === 'Escape') onCancelManageAddress();
                          }}
                          className={clsx(
                            'flex-1 border rounded px-2 py-1 text-sm min-w-0',
                            editingDraft &&
                              (!isValidNotificationEmail(editingDraft) || isEditEmailDuplicate)
                              ? 'border-red-500'
                              : 'border-gray-300'
                          )}
                        />
                        {editingDraft && !isValidNotificationEmail(editingDraft) && (
                          <Error id={`notification-email-edit-${item.id}`}>
                            {t('invalidNotificationEmailAddress')}
                          </Error>
                        )}
                        {editingDraft &&
                          isValidNotificationEmail(editingDraft) &&
                          isEditEmailDuplicate && (
                            <Error id={`notification-email-dup-${item.id}`}>
                              {t('notificationEmailAddressAlreadyExists')}
                            </Error>
                          )}
                      </div>
                    ) : (
                      <span className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate">
                        {item.notificationAddress}
                      </span>
                    )}
                    {!isEditing && !isAddressActionsLocked && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            onStartEditManageAddress(item.id, item.notificationAddress)
                          }
                          className="flex-shrink-0 text-info hover:underline p-0.5"
                          aria-label={t('Edit')}
                        >
                          <Icon icon="mdi:pencil" width={16} height={16} className="text-info" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={isDeletingNotificationEmail}
                          className="flex-shrink-0 text-error hover:underline p-0.5 disabled:opacity-50"
                          aria-label={t('delete')}
                        >
                          <TrashIcon width={16} height={16} className="text-error" />
                        </button>
                      </>
                    )}
                    {isEditing && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => canSaveManageAddress && onSaveManageAddress(item.id)}
                          disabled={!canSaveManageAddress}
                          className="p-0.5 text-info disabled:opacity-50"
                          aria-label={t('Save')}
                        >
                          <AcceptIcon width={18} height={18} className="text-info" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onCancelManageAddress()}
                          disabled={isUpdatingNotificationEmail}
                          className="p-0.5 text-error disabled:opacity-50"
                          aria-label={t('Cancel')}
                        >
                          <ClearIcon width={18} height={18} className="text-error" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {isAddingNewEmail ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newEmailDraft}
                      onChange={(e) => setNewEmailDraft(e.target.value)}
                      placeholder={t('emailAddress')}
                      className={clsx(
                        'flex-1 border rounded px-2 py-1 text-sm',
                        newEmailDraft &&
                          (!isValidNotificationEmail(newEmailDraft) || isNewEmailDuplicate)
                          ? 'border-red-500'
                          : 'border-gray-300'
                      )}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          isValidNotificationEmail(newEmailDraft) &&
                          !isNewEmailDuplicate
                        )
                          handleConfirmAddNew();
                        if (e.key === 'Escape') handleCancelAddNew();
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleConfirmAddNew}
                      disabled={
                        !newEmailDraft.trim() ||
                        !isValidNotificationEmail(newEmailDraft) ||
                        isNewEmailDuplicate ||
                        isAddingNotificationEmail
                      }
                      className="p-0.5 text-info disabled:opacity-50"
                      aria-label={t('Save')}
                    >
                      <AcceptIcon width={18} height={18} className="text-info" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelAddNew}
                      className="p-0.5 text-error"
                      aria-label={t('Cancel')}
                    >
                      <ClearIcon width={18} height={18} className="text-error" />
                    </button>
                  </div>
                  {newEmailDraft && !isValidNotificationEmail(newEmailDraft) && (
                    <Error id="notification-email-new">
                      {t('invalidNotificationEmailAddress')}
                    </Error>
                  )}
                  {newEmailDraft &&
                    isValidNotificationEmail(newEmailDraft) &&
                    isNewEmailDuplicate && (
                      <Error id="notification-email-new-dup">
                        {t('notificationEmailAddressAlreadyExists')}
                      </Error>
                    )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleAddNewClick}
                  disabled={editingId !== null}
                  className="flex items-center gap-1.5 text-info hover:underline text-sm w-fit disabled:opacity-50 disabled:hover:no-underline"
                  aria-label={t('addNew')}
                >
                  <Icon icon="lucide:plus" width={16} height={16} className="text-info" />
                  <span>{t('addNew')}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
